import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  Search,
  Filter,
  Eye,
  Loader2,
  Building,
  MapPin,
  Globe,
  Store,
  Gift,
  BarChart3,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import axios from "axios";

const API = '/api';

const categoryNames = {
  disabled_blue_badge: "Blue Badge",
  adult_with_child: "Adult + Child",
  extra_wide: "Extra Wide",
  nhs_staff: "NHS Staff",
  military: "Military",
  lone_female: "Legacy Category"
};

const STORAGE_KEY = "parkwise_registration_filters";

// Helper to load filters from localStorage with URL param fallback
const loadPersistedFilters = (searchParams) => {
  const stored = localStorage.getItem(STORAGE_KEY);
  let saved = {};
  try {
    if (stored) saved = JSON.parse(stored);
  } catch (e) {
    saved = {};
  }
  
  // URL params take precedence, then localStorage, then defaults
  return {
    search: searchParams.get("search") || saved.search || "",
    category: searchParams.get("category") || saved.category || "all",
    status: searchParams.get("status") || saved.status || "all",
    site: searchParams.get("site") || searchParams.get("site_id") || saved.site || "all",
    scope: searchParams.get("scope") || saved.scope || "all",
    expiring: searchParams.get("expiring") || ""
  };
};

export default function AdminRegistrations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [user, setUser] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize filters from localStorage first, then URL params
  const initialFilters = loadPersistedFilters(searchParams);
  const [search, setSearch] = useState(initialFilters.search);
  const [categoryFilter, setCategoryFilter] = useState(initialFilters.category);
  const [statusFilter, setStatusFilter] = useState(initialFilters.status);
  const [siteFilter, setSiteFilter] = useState(initialFilters.site);
  const [scopeFilter, setScopeFilter] = useState(initialFilters.scope);
  const [expiringFilter, setExpiringFilter] = useState(initialFilters.expiring);

  useEffect(() => {
    checkAuth();
  }, []);

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    const filterState = {
      search,
      category: categoryFilter,
      status: statusFilter,
      site: siteFilter,
      scope: scopeFilter
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filterState));
  }, [search, categoryFilter, statusFilter, siteFilter, scopeFilter]);

  useEffect(() => {
    if (user) {
      fetchRegistrations();
      updateUrlParams();
    }
  }, [search, categoryFilter, statusFilter, siteFilter, scopeFilter, expiringFilter, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchSites();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(response.data);
    } catch (error) {
      console.error("Failed to fetch sites:", error);
    }
  };

  const updateUrlParams = () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (categoryFilter !== "all") params.set("category", categoryFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (siteFilter !== "all") params.set("site", siteFilter);
    if (scopeFilter !== "all") params.set("scope", scopeFilter);
    setSearchParams(params, { replace: true });
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (siteFilter !== "all") params.append("site_id", siteFilter);
      if (scopeFilter !== "all") params.append("entitlement_scope", scopeFilter);
      if (expiringFilter === "soon") params.append("expiring_soon", "true");

      const response = await axios.get(`${API}/registrations?${params.toString()}`, { withCredentials: true });
      setRegistrations(response.data);
    } catch (error) {
      console.error("Failed to fetch registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site ? site.name : siteId || "—";
  };

  const getStatusBadge = (status) => {
    const classes = {
      active: "status-badge status-active",
      expired: "status-badge status-expired",
      suspended: "status-badge status-suspended",
      revoked: "status-badge status-revoked"
    };
    return <span className={classes[status] || "status-badge"}>{status}</span>;
  };

  const getScopeIndicator = (scope, originSiteId) => {
    if (scope === "all_sites") {
      return (
        <div className="flex items-center gap-1 text-blue-400" title="Valid at All Sites">
          <Globe className="w-4 h-4" />
          <span className="text-xs">All Sites</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 text-slate-400" title={`Valid at ${getSiteName(originSiteId)} Only`}>
        <MapPin className="w-4 h-4" />
        <span className="text-xs">Site Only</span>
      </div>
    );
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-registrations">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
          
          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Wallet className="w-5 h-5" /><span>Commercial Dashboard</span>
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Package className="w-5 h-5" /><span>Products & Tariffs</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Registrations
          </h1>
          <p className="text-slate-400">View and manage all parking registrations</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by vehicle, email, or name..."
              className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="search-input"
            />
          </div>
          
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white" data-testid="site-filter">
              <MapPin className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Site" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.site_id} value={site.site_id} className="text-white">{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px] bg-slate-900 border-slate-700 text-white" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Categories</SelectItem>
              <SelectItem value="disabled_blue_badge" className="text-white">Blue Badge</SelectItem>
              <SelectItem value="adult_with_child" className="text-white">Adult + Child</SelectItem>
              <SelectItem value="extra_wide" className="text-white">Extra Wide</SelectItem>
              <SelectItem value="nhs_staff" className="text-white">NHS Staff</SelectItem>
              <SelectItem value="military" className="text-white">Military</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-white" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Statuses</SelectItem>
              <SelectItem value="active" className="text-white">Active</SelectItem>
              <SelectItem value="expired" className="text-white">Expired</SelectItem>
              <SelectItem value="suspended" className="text-white">Suspended</SelectItem>
              <SelectItem value="revoked" className="text-white">Revoked</SelectItem>
            </SelectContent>
          </Select>

          <Select value={scopeFilter} onValueChange={setScopeFilter}>
            <SelectTrigger className="w-[160px] bg-slate-900 border-slate-700 text-white" data-testid="scope-filter">
              <Globe className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Scope" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Scopes</SelectItem>
              <SelectItem value="site_only" className="text-white">Site Only</SelectItem>
              <SelectItem value="all_sites" className="text-white">All Sites</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="admin-card border-slate-800 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : (
            <Table className="admin-table">
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Vehicle</TableHead>
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">Origin Site</TableHead>
                  <TableHead className="text-slate-400">Scope</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Expires</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                      No registrations found
                    </TableCell>
                  </TableRow>
                ) : (
                  registrations.map((reg) => (
                    <TableRow key={reg.registration_id} className="border-slate-800/50 hover:bg-slate-800/30">
                      <TableCell className="font-mono text-white font-medium">
                        {reg.vehicle_registration}
                      </TableCell>
                      <TableCell className="text-slate-300">{reg.full_name}</TableCell>
                      <TableCell className="text-slate-300">
                        {categoryNames[reg.category] || reg.category}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {getSiteName(reg.origin_site_id || reg.site_id)}
                      </TableCell>
                      <TableCell>
                        {getScopeIndicator(reg.entitlement_scope, reg.origin_site_id || reg.site_id)}
                      </TableCell>
                      <TableCell>{getStatusBadge(reg.status)}</TableCell>
                      <TableCell className="text-slate-400">{formatDate(reg.expires_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/registrations/${reg.registration_id}`)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-slate-800"
                          data-testid={`view-${reg.registration_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
